import { useCallback, useEffect, useMemo, useState } from "react";
import { FaCheckCircle, FaClock, FaTimesCircle } from "react-icons/fa";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { isMobileAkanthasAccount } from "./mobileModuleUtils";
import InstallationSubpageLayout from "./installation/InstallationSubpageLayout";
import InstallationCronSection from "./installation/InstallationCronSection";
import { useInstallationCamera } from "./installation/useInstallationCamera";
import { buildInitialCronState } from "./installation/InstallationConfigPanel";
import {
  formatCycleFileTime,
  formatNextWakeupDatetime,
  getRecentCyclesForDisplay,
} from "./installation/installationScheduleUtils";
import {
  buildTimingsAzurePartial,
  isValidCronLine,
} from "./installation/installationConfigUtils";
import { resolveActiveTimingsForSave } from "./installation/installationScheduleModeUtils";
import { writeAzureConfig } from "./installation/installationApi";

function RecentCycleRow({ cycle, t }) {
  const isReceived = Boolean(cycle.log);
  return (
    <div className="installation-row-cycle-row">
      <span className="installation-row-cycle-meta">
        {isReceived
          ? t("installation_schedule_cycle_received")
          : t("installation_schedule_cycle_missing")}
      </span>
      <span className="installation-row-cycle-time">
        {formatCycleFileTime(cycle.name)}
      </span>
      {isReceived ? (
        <FaCheckCircle
          className="installation-row-cycle-icon received"
          aria-hidden
        />
      ) : (
        <FaTimesCircle
          className="installation-row-cycle-icon missing"
          aria-hidden
        />
      )}
    </div>
  );
}

const AppInstallationScheduleDetail = ({ setCookies, saveUser }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const {
    loading,
    configData,
    setConfigData,
    cycleEntries,
    nextWakeupDatetime,
    leftCamera,
    api,
    token,
    reload,
  } = useInstallationCamera(id);

  const [cronState, setCronState] = useState(() =>
    buildInitialCronState(configData),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCronState(buildInitialCronState(configData));
  }, [configData]);

  useEffect(() => {
    if (!isMobileAkanthasAccount()) {
      history.replace(`/app/installationdetails/${id}`);
    }
  }, [history, id]);

  const recentCycles = useMemo(
    () => getRecentCyclesForDisplay(cycleEntries, 3),
    [cycleEntries],
  );

  const saveCron = useCallback(
    async (nextCronState) => {
      const activeTimings = resolveActiveTimingsForSave(nextCronState);
      const invalid = activeTimings.find((line) => !isValidCronLine(line));
      if (invalid) {
        toast.error(t("installation_cron_invalid"));
        return false;
      }

      setSaving(true);
      try {
        const azurePartial = buildTimingsAzurePartial(
          configData,
          activeTimings,
          nextCronState.timingsSave,
        );
        const { output } = await writeAzureConfig(
          api,
          token,
          id,
          configData,
          azurePartial,
        );
        setConfigData(output);
        setCronState(nextCronState);
        toast.success(t("installation_saved"));
        await reload({ silent: true });
        return true;
      } catch {
        toast.error(t("installation_save_failed"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [api, configData, id, reload, setConfigData, t, token],
  );

  return (
    <InstallationSubpageLayout
      pageTitle={t("installation_schedule_mode_label")}
      onBack={() => history.goBack()}
      setCookies={setCookies}
      saveUser={saveUser}
    >
      {isMobileAkanthasAccount() && (
        <>
          <section className="installation-subpage-card">
            <InstallationCronSection
              codeVersion={leftCamera?.codeVersion}
              cronState={cronState}
              onCronSave={saveCron}
              disabled={saving}
              saving={saving}
              adminOnly
            />
          </section>

          <section className="installation-subpage-card">
            <h2 className="installation-subpage-card-title">
              {t("installation_schedule_cycles_title")}
            </h2>
            <div className="installation-row-cycles-list installation-schedule-detail-list">
              <div className="installation-row-cycle-row installation-row-cycle-row-next">
                <span className="installation-row-cycle-meta">
                  {t("installation_schedule_next_wakeup_label")}
                </span>
                <span className="installation-row-cycle-time">
                  {formatNextWakeupDatetime(nextWakeupDatetime)}
                </span>
                <FaClock
                  className="installation-row-cycle-icon next"
                  aria-hidden
                />
              </div>
              {loading && recentCycles.length === 0
                ? Array.from({ length: 3 }).map((_, index) => (
                    <div
                      key={`schedule-loading-${index}`}
                      className="installation-row-cycle-row"
                    >
                      <span className="installation-row-cycle-meta">
                        {t("installation_loading")}
                      </span>
                      <span className="installation-row-cycle-time">…</span>
                      <FaClock className="installation-row-cycle-icon loading" />
                    </div>
                  ))
                : recentCycles.map((cycle, index) => (
                    <RecentCycleRow
                      key={cycle.name || index}
                      cycle={cycle}
                      t={t}
                    />
                  ))}
            </div>
          </section>
        </>
      )}
    </InstallationSubpageLayout>
  );
};

export default AppInstallationScheduleDetail;
